import { ButtonLink, Card, Eyebrow, Heading, Lead, Section } from '../components/ui';
import { usePageTitle } from '../lib/usePageTitle';

const GROUPS = [
  {
    title: 'Verification',
    body: 'What the agent actually does with your acceptance criteria.',
    items: [
      {
        name: 'Criterion-by-criterion verdicts',
        body: 'Every AC and EC in your spec gets a row. A criterion the agent could not exercise is reported as BLOCKED, with the reason — never omitted, and never assumed to pass.',
      },
      {
        name: 'Evidence-backed results',
        body: 'A PASS must cite a stored artefact. The rule is enforced where results are constructed, so no reasoning path can produce an unsupported green.',
      },
      {
        name: 'Deterministic checks first',
        body: 'HTTP probes and page inspections decide verdicts. The model proposes what to look at; it never decides what happened.',
      },
      {
        name: 'Open questions surfaced',
        body: 'Unresolved questions in your spec are listed as untested rather than guessed at.',
      },
    ],
  },
  {
    title: 'Evidence and reporting',
    body: 'What you get to keep after a run.',
    items: [
      {
        name: 'Screenshots and traces',
        body: 'Full-page screenshots, HTTP request and response metadata, and browser console output.',
      },
      {
        name: 'Automatic redaction',
        body: 'Authorization headers, cookies, API keys, tokens and credentials in URLs are stripped before anything reaches disk.',
      },
      {
        name: 'Markdown and JSON reports',
        body: 'One human-readable report and one machine-readable, plus run metadata for your own tooling.',
      },
      {
        name: 'Immutable run history',
        body: 'Each run lands in its own timestamped folder. Nothing is overwritten, so you can diff two runs.',
      },
    ],
  },
  {
    title: 'Execution safety',
    body: 'The boundaries around an agent that can drive a browser and a shell.',
    items: [
      {
        name: 'Command allowlisting',
        body: 'Proposed commands are checked against a denylist of destructive patterns and an allowlist of known programs. Anything unrecognised is refused by default.',
      },
      {
        name: 'Prompt-injection defence',
        body: 'Content from pages, APIs and project files is fenced as untrusted data. Instructions found inside it are reported as findings, never followed.',
      },
      {
        name: 'Origin restriction',
        body: 'The browser only opens origins named in your run instructions. Downloads are refused and the session is discarded afterwards.',
      },
      {
        name: 'Read-only by default',
        body: 'Nothing submits a form or mutates data unless your verification guide explicitly permits it.',
      },
    ],
  },
  {
    title: 'Platform',
    body: 'Accounts, credentials and metering.',
    items: [
      {
        name: 'Two-factor sign-in',
        body: 'A correct password earns an emailed one-time code, not a session. Codes are single-use and attempt-capped.',
      },
      {
        name: 'Rotatable agent credentials',
        body: 'Client secrets are shown once and stored hashed. Rotation keeps the old secret working for a grace period so a running agent is not cut off.',
      },
      {
        name: 'OS-native secret storage',
        body: 'Secrets go to Windows Credential Manager, macOS Keychain or Linux Secret Service — never to a config file.',
      },
      {
        name: 'Server-side metering',
        body: 'Execution hours are measured on the server clock. A tampered local clock cannot buy free time.',
      },
    ],
  },
] as const;

export function Features() {
  usePageTitle('Features — WaveQA');

  return (
    <>
      <Section className="border-b border-hull-800">
        <Eyebrow>Features</Eyebrow>
        <Heading level={1} className="text-white">
          What WaveQA does, precisely
        </Heading>
        <Lead className="mt-5 max-w-2xl">
          WaveQA is a QA agent, not a chatbot. Its job is to turn your acceptance
          criteria into verdicts you can defend in a review.
        </Lead>
      </Section>

      {GROUPS.map((group, index) => (
        <Section
          key={group.title}
          className={index < GROUPS.length - 1 ? 'border-b border-hull-800' : undefined}
        >
          <Heading className="text-white">{group.title}</Heading>
          <Lead className="mt-3 max-w-2xl">{group.body}</Lead>

          <ul className="mt-8 grid gap-4 md:grid-cols-2">
            {group.items.map((item) => (
              <Card as="li" key={item.name}>
                <h3 className="text-base font-semibold text-white">{item.name}</h3>
                <p className="mt-2 text-sm leading-relaxed text-hull-300">{item.body}</p>
              </Card>
            ))}
          </ul>
        </Section>
      ))}

      <Section>
        <Card className="text-center">
          <Heading className="text-white">See it against your own spec</Heading>
          <Lead className="mx-auto mt-3 max-w-xl">
            The Free plan is enough to run a real verification on a real project.
          </Lead>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <ButtonLink to="/register">Get Started</ButtonLink>
            <ButtonLink to="/how-it-works" variant="secondary">
              How It Works
            </ButtonLink>
          </div>
        </Card>
      </Section>
    </>
  );
}
